import {
  DragHandle,
  getErrorMessage,
  getTextFilterProps,
  insertCopiedAndRemoveCutRows,
  onSortEnd,
} from './utils';
import { UpdatePopover } from './UpdatePopover';
import { Options } from './Options';
import { HeaderButtons } from './HeaderButtons';
import {
  AlertMessage,
  checkDuplicates,
  updateAndSortFields,
} from '../common/output-layout-utils';
import { generateId } from '@/app/core/utils';
import { EditableCell } from '@/app/core/components/EditableCell';
import {
  CSSProperties,
  Key,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Table } from 'antd';
import {
  AnyRecord,
  FieldType,
  OutputLayoutField,
  ProgramConfigCategory,
} from '@ssar-app/domain';
import {
  Flex,
  Icon,
  InputProps,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { AiFillInfoCircle, AiOutlineClose } from 'react-icons/ai/index.js';
import { ModalFunctionProps } from '@ssar-app/uikit';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  defaultDropAnimation,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import { ErrorOption } from 'react-hook-form/dist/types/form';
import hotkeys from 'hotkeys-js';
import { isEqual } from 'lodash';
import { useForm } from 'react-hook-form';

interface CustomLayoutProps {
  outputFields: OutputLayoutField[];
  refreshLayout: (isCustomized: boolean) => void;
  showWarningPopup: (props: ModalFunctionProps) => void;
  clearErrors: (name?: string | string[] | undefined) => void;
  setError: (name: string, error: ErrorOption) => void;
  isActive?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  actionsHandler: any;
  setFields: (value: OutputLayoutField[]) => void;
}

export const CustomOutputLayout = (props: CustomLayoutProps) => {
  const {
    outputFields,
    refreshLayout,
    showWarningPopup,
    clearErrors,
    setError,
    isActive,
    actionsHandler,
    setFields,
  } = props;
  const toast = useToast();
  const [fromIndex, setFromIndex] = useState<number>();
  const [pageConfig, setPageConfig] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 1, pageSize: 10 });

  const [rows, setRows] = useState<OutputLayoutField[]>(outputFields);
  const [copiedRows, setCopiedRows] = useState<OutputLayoutField[]>([]);
  const [isCutMode, setIsCutMode] = useState(false);
  const [duplicateHeaders, setDuplicateHeaders] = useState<
    Record<string, number[]>
  >({});
  const [hasDuplicates, setHasDuplicates] = useState(false);

  const { control, getValues, reset } = useForm<OutputLayoutField>();
  const [editingKey, setEditingKey] = useState('');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const rowIds = useMemo(() => rows.map((row) => row.meta?.id), [rows]);
  const SortableWrapper = (props: AnyRecord) => {
    return (
      <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
        <tbody {...props} />
      </SortableContext>
    );
  };

  const SortableRow = (props: AnyRecord) => {
    const { setNodeRef, transform, transition, isDragging } = useSortable({
      id: props?.['data-row-key'],
    });
    const style: CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      boxShadow: isDragging ? `var(--chakra-shadows-lg)` : '',
    };
    return <tr {...props} ref={setNodeRef} style={style} />;
  };
  const searchInputRef = useRef<HTMLInputElement>(null);

  const columns = [
    {
      title: '',
      align: 'center' as const,
      render: (_val: string, record: OutputLayoutField) => {
        return (
          <DragHandle
            active={selectedFields.includes(record.meta?.id)}
            id={record.meta?.id}
          />
        );
      },
      width: '48px',
    },
    {
      title: '',
      align: 'center' as const,
      dataIndex: ['meta', 'index'],
      width: '56px',
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: '100px',
      filters: [
        { text: 'Experian', value: 'Experian' },
        { text: 'Client', value: 'Client' },
        { text: 'User', value: 'User' },
      ],
      onFilter: (value: string, record: OutputLayoutField) =>
        record.source && record.source.indexOf(value as string) === 0,
    },
    {
      title: 'Field Type',
      dataIndex: 'category',
      key: 'category',
      width: '104px',
      render: (value: string) => {
        return value ? (
          value === ProgramConfigCategory.OutputRequired ? (
            <Text color='purple.600'>Required</Text>
          ) : value === ProgramConfigCategory.Custom ||
            value === ProgramConfigCategory.ClientInput ? (
            <Text>Optional</Text>
          ) : (
            <Text>Selected</Text>
          )
        ) : (
          ''
        );
      },
      filters: Object.entries(FieldType).map(([text, value]) => ({
        text: text,
        value: value,
      })),
      onFilter: (value: string, record: OutputLayoutField) =>
        value === FieldType.Optional
          ? record.category === ProgramConfigCategory.Custom ||
            record.category === ProgramConfigCategory.ClientInput
          : value === FieldType.Required
          ? record.category === ProgramConfigCategory.OutputRequired
          : record.category !== ProgramConfigCategory.OutputRequired &&
            record.category !== ProgramConfigCategory.Custom &&
            record.category !== ProgramConfigCategory.ClientInput,
    },
    {
      title: 'Field Name',
      dataIndex: 'name',
      key: 'name',
      width: '250px',
      onFilter: (value: string, record: OutputLayoutField) =>
        record.name && record.name.includes(value.toUpperCase()),
      ...getTextFilterProps('Field Name', searchInputRef),
    },
    {
      title: 'Header Name',
      dataIndex: 'headerName',
      key: 'headerName',
      editable: true,
      width: '250px',
      render: (val: string) => {
        const dupHeaders = duplicateHeaders[val?.toUpperCase()] || [];
        return dupHeaders.length > 1 ? (
          <>
            <Tooltip
              placement='top-start'
              label={`Duplicate Header at rows: ${
                dupHeaders.length > 5
                  ? `${dupHeaders?.slice(0, 5).join(', ')}.. etc`
                  : `${dupHeaders?.join(', ')}`
              }`}
            >
              <Flex gap={2} bg='red.100' p={1} alignItems='center'>
                <Icon as={AiFillInfoCircle} color='red.500' fontSize='md' />{' '}
                {val}
              </Flex>
            </Tooltip>
          </>
        ) : (
          val
        );
      },
      onFilter: (value: string, record: OutputLayoutField) =>
        record.headerName && record.headerName.includes(value.toUpperCase()),
      ...getTextFilterProps('Header Name', searchInputRef),
    },
    {
      title: 'Length',
      dataIndex: 'fieldLength',
      key: 'fieldLength',
      editable: true,
      width: '140px',
      onFilter: (value: string, record: OutputLayoutField) =>
        record.fieldLength.toString() === value.toString(),
      ...getTextFilterProps('Length', searchInputRef),
    },
    {
      title: 'Start',
      dataIndex: ['meta', 'start'],
      key: 'start',
      width: '80px',
      render: (value: string, record: OutputLayoutField) => {
        const editing = isEditing(record);
        return editing ? (
          <UpdatePopover
            hasError={!!getErrorMessage(record).errorMsg}
            onUpdate={() => onUpdate(record.meta?.id, true, true)}
            onCancel={onCancel}
          >
            {value}
          </UpdatePopover>
        ) : (
          value
        );
      },
      onFilter: (value: string, record: OutputLayoutField) =>
        record.meta && record.meta.start.toString() === value.toString(),
      ...getTextFilterProps('Start', searchInputRef, 'number'),
    },
    {
      title: 'End',
      dataIndex: ['meta', 'end'],
      key: 'end',
      width: '80px',
      onFilter: (value: string, record: OutputLayoutField) =>
        record.meta && record.meta.end.toString() === value.toString(),
      ...getTextFilterProps('End', searchInputRef, 'number'),
    },
    {
      title: 'Constant Value',
      dataIndex: 'constantValue',
      key: 'constantValue',
      editable: true,
      width: '150px',
      onFilter: (value: number, record: OutputLayoutField) =>
        record.constantValue &&
        record.constantValue.toString() === value.toString(),
      render: (value: string) => (value ? value : '--'),
      ...getTextFilterProps('Constant Value', searchInputRef),
    },
    {
      title: '',
      key: 'actions',
      align: 'center' as const,
      width: '44px',
      render: (_val: string, record: OutputLayoutField) =>
        editingKey === record.meta?.id ? (
          ''
        ) : (
          <Options
            addRowAt={addRowAt}
            onEdit={(id) => setEditingKey(id)}
            index={record.meta?.index - 1}
            record={record}
            removeRows={removeRows}
            pasteRowsAt={pasteRowsAt}
            hidePasteBtns={copiedRows.length === 0}
          />
        ),
    },
  ].filter((a) => a !== null);
  const mergedColumns = columns.map((col) => {
    return {
      ...col,
      onCell: (record: OutputLayoutField) => {
        const errorDetails = getErrorMessage(record);
        const showError = errorDetails.field === col.dataIndex;
        return {
          ...(col.editable
            ? {
                record,
                inputType: col.dataIndex === 'fieldLength' ? 'number' : 'text',
                inputProps: {
                  onChange: () => onUpdate(record.meta?.id, false),
                  placeholder:
                    col.dataIndex === 'constantValue' ? 'Optional' : 'Required',
                  title: showError ? errorDetails.errorMsg : '',
                  ...(col.dataIndex === 'fieldLength'
                    ? { min: record.constantValue?.length || 1 }
                    : {}),
                } as InputProps,
                control: control,
                dataIndex: col.dataIndex,
                title: col.title,
                editing:
                  record.category === ProgramConfigCategory.Custom ||
                  (record.category === ProgramConfigCategory.ClientInput
                    ? col.dataIndex === 'fieldLength'
                    : col.dataIndex !== 'constantValue')
                    ? isEditing(record)
                    : false,
              }
            : {}),
          ...(col.key !== 'actions'
            ? {
                onClick: (e: MouseEvent) => {
                  const index = record.meta?.index - 1;
                  if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    setSelectedFields((items) => {
                      setFromIndex(index);
                      if (items.includes(record.meta?.id)) {
                        return items.filter((id) => record.meta?.id !== id);
                      }
                      items.push(record.meta?.id);
                      return Array.from(new Set(items).values());
                    });
                  } else if (e.shiftKey) {
                    e.preventDefault();
                    setSelectedFields((items) => {
                      const ids =
                        fromIndex !== undefined && index !== undefined
                          ? [
                              ...items,
                              ...(fromIndex <= index
                                ? rows.slice(fromIndex, index + 1)
                                : rows.slice(index, fromIndex + 1)
                              ).map((row) => row.meta?.id),
                            ]
                          : items;
                      return Array.from(new Set(ids).values());
                    });
                  } else {
                    setSelectedFields([]);
                    setFromIndex(undefined);
                  }
                },
              }
            : {}),
        };
      },
    };
  });

  const isEditing = (record: OutputLayoutField) =>
    record.meta?.id === editingKey;

  const onCancel = () => {
    const filteredRow = outputFields.find((row) => row.meta?.id === editingKey);
    const newRows = rows
      .map((row) => {
        if (row.meta?.id === editingKey) {
          return filteredRow ? filteredRow : null;
        }
        return row;
      })
      .filter((row) => !!row) as OutputLayoutField[];
    setEditingKey('');
    setRows(newRows);
  };

  const onUpdate = async (
    key: Key,
    updateEditingKey = true,
    updateFinalFields = false,
  ) => {
    try {
      const row = getValues() as OutputLayoutField;
      let newRows = [...rows];
      const index = newRows.findIndex((item) => key === item.meta?.id);
      if (index > -1) {
        const item = newRows[index];
        newRows.splice(index, 1, {
          ...item,
          ...row,
        });
        if (updateEditingKey) {
          newRows = updateAndSortFields(newRows);
          setEditingKey('');
        }
        setRows(newRows);
        updateFinalFields && setFields(newRows);
      } else {
        newRows.push(row);
        if (updateEditingKey) {
          newRows = updateAndSortFields(newRows);
          setEditingKey('');
        }
        setRows(newRows);
        updateFinalFields && setFields(newRows);
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const addRowAt = (index: number) => {
    const newId = generateId();
    const newRows = [
      ...rows.slice(0, index),
      {
        meta: { id: newId },
        constantValue: '',
        source: 'User',
        category: ProgramConfigCategory.Custom,
        outputOrder: 0,
      } as unknown as OutputLayoutField,
      ...rows.slice(index),
    ].filter((row) => !!row);
    const { page, pageSize } = pageConfig;
    if (page * pageSize === index) {
      setPageConfig({ page: page + 1, pageSize });
    }
    setEditingKey(newId);
    setRows(newRows);
  };

  const removeRows = (rowId?: string) => {
    const newRows = updateAndSortFields(
      rowId
        ? rows.filter((row) => row.meta?.id !== rowId)
        : rows.filter((row) => !selectedFields.includes(row.meta?.id)),
    );
    rowId
      ? setSelectedFields((selectedFields) =>
          selectedFields.filter((key) => key !== rowId),
        )
      : setSelectedFields([]);
    setRows(newRows);
    setFields(newRows);
  };

  const cutSelectedRows = () => {
    const copyRows: OutputLayoutField[] = [];
    const updatedRows = rows.map((row) => {
      if (selectedFields.indexOf(row.meta?.id) >= 0) {
        copyRows.push(row);
        return { ...row, meta: { ...row.meta, wasCut: true } };
      } else {
        return row;
      }
    });
    setCopiedRows(copyRows);
    setRows(updatedRows);
    setIsCutMode(true);
    setSelectedFields([]);
  };

  const showToast = (msg?: string) => {
    toast.closeAll();
    toast({
      description: (
        <Flex alignItems='center'>
          {msg}
          <Icon
            as={AiOutlineClose}
            onClick={() => toast.closeAll()}
            position='absolute'
            right='4'
            cursor='pointer'
          />
        </Flex>
      ),
      status: 'info',
      position: 'top',
      variant: 'subtle',
      duration: 2000,
    });
  };

  const handleFunc = (execFn: boolean, fn: () => void, alertMsg?: string) => {
    if (execFn) {
      fn();
    } else {
      toast.closeAll();
      alertMsg && showToast(alertMsg);
    }
  };

  const handlePasteShortcut = (beforeIndex?: boolean) => {
    if (selectedFields?.[0]) {
      const index =
        rows.find((row) => row.meta?.id === selectedFields?.[0])?.meta?.index -
        1;
      pasteRowsAt(beforeIndex ? index : index + 1);
    } else {
      showToast('Please select a row before pasting');
    }
  };

  const handleUndoRedo = (fn: () => void) => {
    fn();
    cancelCut();
  };

  const handleAction = useCallback(
    (action: 'undo' | 'redo' | 'pasteBefore' | 'pasteAfter' | 'cut') => {
      switch (action) {
        case 'undo':
          handleFunc(actionsHandler.canUndo, () =>
            handleUndoRedo(actionsHandler.undo),
          );
          break;
        case 'redo':
          handleFunc(actionsHandler.canRedo, () =>
            handleUndoRedo(actionsHandler.redo),
          );
          break;
        case 'cut':
          handleFunc(
            !!selectedFields.length,
            isCutMode ? () => null : cutSelectedRows,
            'Please select the row(s) before doing the action Cut.',
          );
          break;
        case 'pasteBefore':
          handleFunc(
            !!copiedRows.length,
            () => handlePasteShortcut(true),
            'Please cut the row(s) before pasting.',
          );
          break;
        case 'pasteAfter':
          handleFunc(
            !!copiedRows.length,
            () => handlePasteShortcut(false),
            'Please cut the row(s) before pasting.',
          );
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      actionsHandler.canRedo,
      actionsHandler.canUndo,
      copiedRows,
      selectedFields.length,
    ],
  );

  const pasteRowsAt = (index: number) => {
    const updatedRows = insertCopiedAndRemoveCutRows(rows, copiedRows, index);
    setRows(updatedRows);
    setFields(updatedRows);
    setIsCutMode(false);
    setCopiedRows([]);
  };

  const cancelCut = () => {
    setRows(
      rows.map((row) => ({ ...row, meta: { ...row.meta, wasCut: false } })),
    );
    setIsCutMode(false);
    setCopiedRows([]);
  };

  const LayoutTable = useMemo(() => {
    const hasRequiredRows = selectedFields.some((id) => {
      const category = rows.find((row) => row.meta?.id === id)?.category;
      return (
        category !== ProgramConfigCategory.Custom &&
        category !== ProgramConfigCategory.ClientInput
      );
    });
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          const result = onSortEnd(rows, selectedFields, active.id, over?.id);
          if (result?.length) {
            setRows(result);
            setFields(result);
          }
        }}
        modifiers={[restrictToVerticalAxis]}
      >
        <Table
          size='small'
          className='custom-output-layout-table'
          dataSource={rows}
          columns={mergedColumns}
          components={{
            body: {
              cell: EditableCell,
              ...(editingKey
                ? {}
                : {
                    wrapper: SortableWrapper,
                    row: SortableRow,
                  }),
            },
          }}
          showSorterTooltip={false}
          bordered
          pagination={{
            hideOnSinglePage: false,
            defaultCurrent: 1,
            pageSize: pageConfig.pageSize,
            current: pageConfig.page,
            onChange: (page, pageSize) => setPageConfig({ page, pageSize }),
            showTotal: (total) =>
              `Selected ${selectedFields.length} of ${total} field(s)`,
            position: ['bottomLeft'],
            showSizeChanger: true,
            simple: false,
          }}
          onRow={(record) => ({
            onMouseDown: (e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey) {
                e.preventDefault();
              }
            },
            index: record.meta?.index - 1,
            key: record.meta?.id,
          })}
          rowSelection={{
            selectedRowKeys: selectedFields,
            onChange: (selectedRowKeys): void => {
              setSelectedFields(selectedRowKeys.map((a) => a.toString()));
            },
            getCheckboxProps: (record) => ({
              disabled: record.meta?.id === editingKey,
            }),
            preserveSelectedRowKeys: true,
            columnWidth: '48px',
          }}
          rowClassName={(record) =>
            (editingKey && record.meta?.id !== editingKey) ||
            (record.meta?.wasCut && isCutMode)
              ? 'blurred-row'
              : ''
          }
          rowKey={(row) => row.meta?.id || ''}
          title={() => (
            <HeaderButtons
              disableRemove={selectedFields.length === 0}
              onReset={() =>
                showWarningPopup({
                  onOk: () => {
                    setEditingKey('');
                    refreshLayout(true);
                  },
                  showCancel: true,
                })
              }
              onRemove={() => removeRows()}
              cutProps={{
                disabled:
                  selectedFields.length === 0 && copiedRows.length === 0,
                onClick: isCutMode ? cancelCut : cutSelectedRows,
              }}
              isCutMode={isCutMode}
              undoProps={{
                disabled: !actionsHandler.canUndo,
                onClick: () => handleUndoRedo(actionsHandler.undo),
              }}
              redoProps={{
                disabled: !actionsHandler.canRedo,
                onClick: () => handleUndoRedo(actionsHandler.redo),
              }}
              hasRequiredRows={hasRequiredRows}
            />
          )}
        />
        <DragOverlay
          modifiers={[restrictToParentElement]}
          dropAnimation={{
            ...defaultDropAnimation,
            sideEffects: () => () => null,
          }}
        />
      </DndContext>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergedColumns, rows, selectedFields]);

  useEffect(() => {
    reset({});
    editingKey && reset(rows.find((row) => row.meta?.id === editingKey) || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingKey]);

  useEffect(() => {
    const updatedFields = outputFields.map((field) => ({
      ...field,
      meta: { ...field.meta, wasCut: false },
    }));
    setRows(updatedFields);

    const { duplicates, containsDups } = checkDuplicates(outputFields);
    setDuplicateHeaders(duplicates);
    setHasDuplicates(containsDups);
    !containsDups
      ? clearErrors('fields')
      : setError('fields', {} as ErrorOption);

    actionsHandler.set(outputFields);
    setSelectedFields([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputFields]);

  useEffect(() => {
    const areOutputFieldsEqual = isEqual(rows, actionsHandler.tableRows);
    if (!areOutputFieldsEqual) {
      setRows(actionsHandler.tableRows);
      setFields(actionsHandler.tableRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionsHandler.tableRows]);

  useEffect(() => {
    if (isActive) {
      hotkeys(
        'ctrl+x,ctrl+y,ctrl+z,ctrl+v+up,ctrl+v+down',
        (event, handler) => {
          event.preventDefault();
          switch (handler.key) {
            case 'ctrl+x':
              handleAction('cut');
              break;
            case 'ctrl+y':
              handleAction('redo');
              break;
            case 'ctrl+z':
              handleAction('undo');
              break;
            case 'ctrl+v+up':
              handleAction('pasteBefore');
              break;
            case 'ctrl+v+down':
              handleAction('pasteAfter');
              break;
          }
          event.stopPropagation();
        },
      );
    } else {
      hotkeys.unbind();
    }
    return () => {
      hotkeys.unbind();
    };
  }, [handleAction, isActive]);

  return (
    <>
      <Flex flexDir='column' w='full' py={2}>
        <AlertMessage
          type='error'
          isOpen={hasDuplicates}
          message='Fields contain duplicate headers.'
        />
        <AlertMessage
          type='warning'
          isOpen={isCutMode}
          message='The cut fields will remain in the layout if they are not pasted anywhere.'
        />
      </Flex>
      {LayoutTable}
    </>
  );
};
